import { useEffect, useMemo, useState } from 'react'
import { Share2 } from 'lucide-react'
import { useReducedMotion } from 'framer-motion'
import type { BlockType, MistakeRecord, PrepStats } from '../../types'
import type { Milestone } from '../../utils/milestones'
import { formatMilestoneNumeral, getNextMilestone } from '../../utils/milestones'
import { BLOCK_TRANSITION_CONFIG } from '../../utils/learningUtils'
import { useLocale } from '../../i18n/context'
import { getCurrentMilestoneField, renderShareImage, warmShareImageFonts } from '../../utils/shareImage'
import type { MilestoneShareLabels } from '../../utils/shareImage'
import { shareImageFile } from '../../utils/shareLink'
import MistakesReviewSheet from './MistakesReviewSheet'
import DeckStatusBadge from '../shared/DeckStatusBadge'
import MilestoneSharePreviewSheet from './MilestoneSharePreviewSheet'
import { CountUpNumeral, MilestoneConfetti, SpringNumeral } from './MilestoneCelebration'

function StatItem({ value, label, color }: { value: string | number; label: string; color: string }) {
  return (
    <div style={{ textAlign: 'center', minWidth: 56 }}>
      <div style={{ fontSize: 26, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{label}</div>
    </div>
  )
}

export default function LearningComplete({
  stats, wordsLearnedToday, todayCards, dailyCardGoal, streakDays,
  onContinueLearning, continueUnavailableReason, onGoHome, mistakes, blockStats, prepStats, milestone: milestoneProp,
  totalWordsKnown, isPractice = false,
}: {
  stats: { total: number; correct: number; wrong: number }
  wordsLearnedToday: number
  todayCards?: number
  dailyCardGoal?: number
  streakDays?: number
  onContinueLearning?: () => void
  continueUnavailableReason?: string
  onGoHome: () => void
  /** This session's wrong answers, one entry per card — opens a read-only review sheet. */
  mistakes?: MistakeRecord[]
  blockStats?: Record<string, { correct: number; wrong: number }>
  prepStats?: PrepStats | null
  /** Streak or vocabulary milestone hit by this session, if any — swaps the standard
   *  headline for the milestone block and changes the Share label. See detectMilestone. */
  milestone?: Milestone | null
  /** User's current total learned-words count (post-session) — reconciles the vocabulary
   *  milestone numeral (a threshold, not a total) against the real total in the support line. */
  totalWordsKnown?: number
  /** ADR-112 Decision 6: the session was practice — no answer in it changed a level. */
  isPractice?: boolean
}) {
  // A practice session cannot have moved the words-known count, so a vocabulary
  // milestone here would claim a change the session did not make (ADR-112
  // Decision 6). A streak milestone stays: practice counts toward the daily goal
  // and the streak exactly as a ladder session does (Decision 7).
  const milestone = isPractice && milestoneProp?.type === 'vocabulary' ? null : milestoneProp
  const { tr, tag } = useLocale()
  const pct = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0
  const [isMistakesOpen, setIsMistakesOpen] = useState(false)
  const [isMilestoneShareOpen, setIsMilestoneShareOpen] = useState(false)
  const [isSharing, setIsSharing] = useState(false)
  const [shareStatus, setShareStatus] = useState<string | null>(null)
  const reducedMotion = !!useReducedMotion()
  const nextMilestone = milestone
    ? getNextMilestone(milestone, milestone.type === 'vocabulary' ? (totalWordsKnown ?? milestone.value) : milestone.value)
    : null

  const isPrep = !!prepStats
  const goalMet = !isPrep && dailyCardGoal != null && todayCards != null && todayCards >= dailyCardGoal && dailyCardGoal > 0
  const heading = isPrep ? tr.complete.prepared : goalMet ? tr.complete.goalReached : pct === 100 ? tr.complete.perfect : tr.complete.sessionDone

  useEffect(() => {
    if (document.getElementById('learn-complete-kf')) return
    const s = document.createElement('style')
    s.id = 'learn-complete-kf'
    s.textContent = `
      @keyframes completeFadeUp {
        from { opacity: 0; transform: translateY(16px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      @keyframes milestoneSpringIn {
        from { transform: scale(0.5); opacity: 0; }
        to   { transform: scale(1); opacity: 1; }
      }
      .lc-root {
        position: relative;
        min-height: 100vh;
        min-height: 100dvh;
        background: var(--bg-page);
        display: flex; flex-direction: column;
        align-items: center; justify-content: center;
        gap: 20px; padding: 20px 16px;
        box-sizing: border-box;
      }
      .lc-content {
        position: relative; z-index: 2;
        display: flex; flex-direction: column;
        align-items: center; justify-content: center;
        gap: 20px; width: 100%;
      }
      .lc-stats { padding: 16px 28px; }
      .lc-card  { padding: 14px 18px; }
      .lc-btn-primary {
        padding: 14px 40px; border-radius: 50px; border: none;
        background: var(--accent-fill); color: var(--text-on-accent);
        font-size: 15px; font-weight: 600; cursor: pointer;
        width: 100%; min-height: 44px;
      }
      .lc-btn-secondary {
        padding: 12px 40px; border-radius: 50px;
        border: 1px solid var(--border); background: transparent;
        color: var(--text-secondary); font-size: 15px; cursor: pointer;
        width: 100%; min-height: 44px;
      }
      .lc-btn-link {
        background: none; border: none; padding: 4px 8px;
        color: var(--text-secondary); font-size: 13px; cursor: pointer;
        text-decoration: underline;
        text-underline-offset: 3px;
        min-height: 44px; width: 100%;
      }
      .lc-btn-share {
        display: flex; align-items: center; justify-content: center; gap: 8px;
        padding: 12px 40px; border-radius: 50px;
        border: 1px solid var(--border); background: transparent;
        color: var(--text-primary); font-size: 15px; font-weight: 600; cursor: pointer;
        width: 100%; min-height: 44px;
      }
      .lc-btn-share:disabled { opacity: 0.6; cursor: default; }
      .lc-btn-share-elevated {
        display: flex; align-items: center; justify-content: center; gap: 8px;
        padding: 12px 40px; border-radius: 50px;
        border: 1px solid var(--accent); background: var(--accent-soft);
        color: var(--accent); font-size: 15px; font-weight: 600; cursor: pointer;
        width: 100%; min-height: 44px;
      }
      .lc-btn-share-elevated:disabled { opacity: 0.6; cursor: default; }
      /* Shrink decorative elements on short viewports; buttons stay the same */
      @media (max-height: 680px) {
        .lc-root  { gap: 14px !important; padding: 14px 16px !important; }
        .lc-stats { padding: 12px 22px !important; }
        .lc-card  { padding: 10px 14px !important; }
      }
      @media (max-height: 560px) {
        .lc-root  { gap: 10px !important; padding: 10px 16px !important; }
      }
    `
    document.head.appendChild(s)
  }, [])

  // Pay the canvas font-rasterization cold-start cost now, well before the user can
  // reach the share sheet, so its own renders never pay it (see shareImage.ts).
  useEffect(() => {
    if (milestone) warmShareImageFonts()
  }, [milestone])

  const goalProgress = (dailyCardGoal && dailyCardGoal > 0 && todayCards != null)
    ? Math.min(todayCards / dailyCardGoal, 1)
    : null

  // Primary CTA: "Continue" when cards remain, "Go home" otherwise
  const primaryIsContinue = !!onContinueLearning

  // Static (locale-resolved) content for the milestone share card — everything the
  // renderer needs except the field, which the preview sheet toggles independently.
  const milestoneShareLabels = useMemo<MilestoneShareLabels | null>(() => {
    if (!milestone) return null
    const m = tr.complete.milestone
    const isVocab = milestone.type === 'vocabulary'
    const total = isVocab ? (totalWordsKnown ?? milestone.value) : milestone.value
    return {
      brand: 'CardSmith',
      category: m.category,
      eyebrow: isVocab ? m.vocabularyEyebrow : m.streakEyebrow,
      numeral: formatMilestoneNumeral(milestone.value, tag),
      label: isVocab ? m.wordsLabel(milestone.value) : m.streakLabel,
      totalValue: formatMilestoneNumeral(total, tag),
      totalSuffix: isVocab ? m.totalSuffixVocabulary(total) : m.totalSuffixStreak,
      footer: window.location.origin.replace(/^https?:\/\//, ''),
    }
  }, [milestone, totalWordsKnown, tr, tag])

  async function handleShare() {
    if (isSharing || milestone) return
    setIsSharing(true)
    setShareStatus(null)
    try {
      const origin = window.location.origin
      const blob = await renderShareImage(
        { correct: stats.correct, wrong: stats.wrong, accuracy: pct, wordsLearnedToday, streakDays },
        {
          brand: 'CardSmith',
          heading,
          correctLabel: tr.complete.correct,
          wrongLabel: tr.complete.wrong,
          accuracyLabel: tr.complete.accuracy,
          newWordsTodayLabel: tr.complete.newWordsToday,
          streakLabel: streakDays != null && streakDays > 0 ? tr.complete.daysStreak(streakDays) : undefined,
          footerUrl: origin.replace(/^https?:\/\//, ''),
        },
      )
      const shareText = tr.complete.shareCaption(wordsLearnedToday, origin)
      const result = await shareImageFile({
        blob,
        filename: 'cardsmith-progress.png',
        title: tr.complete.shareTitle,
        text: shareText,
      })
      if (result === 'downloaded') setShareStatus(tr.complete.shareSavedFallback)
    } catch {
      // Sharing must never break the completion screen — surface a status line instead.
      setShareStatus(tr.complete.shareFailed)
    } finally {
      setIsSharing(false)
    }
  }

  async function handleMilestoneShare(blob: Blob) {
    if (!milestone || isSharing) return
    setIsSharing(true)
    setShareStatus(null)
    try {
      const origin = window.location.origin
      const m = tr.complete.milestone
      const shareText = milestone.type === 'vocabulary'
        ? m.shareCaptionVocabulary(milestone.value, origin)
        : m.shareCaptionStreak(milestone.value, origin)
      const result = await shareImageFile({
        blob,
        filename: 'cardsmith-milestone.png',
        title: tr.complete.shareTitle,
        text: shareText,
      })
      if (result === 'downloaded') setShareStatus(tr.complete.shareSavedFallback)
    } catch {
      // Sharing must never break the preview sheet — surface a status line instead.
      setShareStatus(tr.complete.shareFailed)
    } finally {
      setIsSharing(false)
    }
  }

  return (
    <div className="lc-root">
      {milestone && <MilestoneConfetti active={!reducedMotion} />}
      <div className="lc-content">
      {/* Heading — replaced by the milestone block when this session hit one */}
      {milestone ? (
        <div style={{ textAlign: 'center', animation: 'completeFadeUp 0.4s 0.2s both' }}>
          <div style={{
            fontSize: 11, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase',
            color: 'var(--accent)', marginBottom: 18,
          }}>
            {milestone.type === 'vocabulary' ? tr.complete.milestone.vocabularyEyebrow : tr.complete.milestone.streakEyebrow}
          </div>
          {milestone.type === 'vocabulary' ? (
            <CountUpNumeral target={milestone.value} reducedMotion={reducedMotion} />
          ) : (
            <SpringNumeral value={milestone.value} reducedMotion={reducedMotion} />
          )}
          <div style={{ marginTop: 12, fontSize: 17, fontWeight: 500, color: 'var(--text-primary)' }}>
            {milestone.type === 'vocabulary' ? tr.complete.milestone.wordsLabel(milestone.value) : tr.complete.milestone.streakLabel}
          </div>
          <p style={{ margin: '12px auto 0', maxWidth: 280, fontSize: 13.5, lineHeight: 1.55, color: 'var(--text-secondary)' }}>
            {milestone.type === 'vocabulary'
              ? tr.complete.milestone.supportVocabulary(milestone.value, totalWordsKnown ?? milestone.value)
              : tr.complete.milestone.supportStreak(milestone.value)}
          </p>
          {nextMilestone && (
            <p style={{ margin: '6px auto 0', maxWidth: 280, fontSize: 12.5, color: 'var(--text-secondary)', opacity: 0.85 }}>
              {milestone.type === 'vocabulary'
                ? tr.complete.milestone.nextVocabulary(nextMilestone.remaining)
                : tr.complete.milestone.nextStreak(nextMilestone.remaining)}
            </p>
          )}
        </div>
      ) : (
        <div style={{ textAlign: 'center', animation: 'completeFadeUp 0.4s 0.2s both' }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: goalMet ? 'var(--accent)' : 'var(--text-primary)', margin: '0 0 6px' }}>
            {heading}
          </h1>
          {isPrep && prepStats ? (
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>
              {tr.complete.challengingWords(stats.correct, prepStats.challenging_count)}
            </p>
          ) : (
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>
              {tr.complete.reviewed(stats.total)}
            </p>
          )}
        </div>
      )}

      {isPractice && (
        <div
          data-testid="practice-note"
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
            animation: 'completeFadeUp 0.4s 0.25s both',
          }}
        >
          <DeckStatusBadge status="practice" size="sm" />
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0, textAlign: 'center' }}>
            {tr.complete.practiceNote}
          </p>
        </div>
      )}

      {/* Stats row */}
      <div className="lc-stats" style={{
        display: 'flex', gap: 20,
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
        borderRadius: 16,
        animation: 'completeFadeUp 0.4s 0.3s both',
      }}>
        <StatItem value={stats.correct} label={tr.complete.correct} color="var(--success)" />
        <div style={{ width: 1, background: 'var(--border)' }} />
        <StatItem value={stats.wrong} label={tr.complete.wrong} color="var(--danger)" />
        <div style={{ width: 1, background: 'var(--border)' }} />
        <StatItem value={`${pct}%`} label={tr.complete.accuracy} color="var(--accent)" />
      </div>

      {/* Per-block breakdown — only shown when multiple blocks had activity */}
      {blockStats && Object.keys(blockStats).length > 1 && (
        <div className="lc-card" style={{
          width: '100%', maxWidth: 340,
          background: 'var(--bg-surface)', border: '1px solid var(--border)',
          borderRadius: 16,
          animation: 'completeFadeUp 0.4s 0.33s both',
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
            {tr.complete.bySection}
          </div>
          {(Object.entries(blockStats) as [string, { correct: number; wrong: number }][]).map(([type, s]) => {
            const config = BLOCK_TRANSITION_CONFIG[type as BlockType]
            const bTotal = s.correct + s.wrong
            if (bTotal === 0 || !config) return null
            const bPct = Math.round((s.correct / bTotal) * 100)
            return (
              <div key={type} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>
                  {config.label}
                </span>
                <span style={{
                  fontSize: 13, fontWeight: 600,
                  color: bPct === 100 ? 'var(--success)' : bPct >= 70 ? 'var(--accent)' : 'var(--text-secondary)',
                }}>
                  {tr.complete.blockCard(bPct, bTotal)}
                </span>
              </div>
            )
          })}
        </div>
      )}

      {/* Daily goal progress */}
      {goalProgress !== null && dailyCardGoal != null && todayCards != null && (
        <div className="lc-card" style={{
          width: '100%', maxWidth: 340,
          background: 'var(--bg-surface)', border: `1.5px solid ${goalMet ? 'var(--accent)' : 'var(--border)'}`,
          borderRadius: 16,
          animation: 'completeFadeUp 0.4s 0.35s both',
          transition: 'border-color 0.3s',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {tr.complete.todaysGoal}
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: goalMet ? 'var(--accent)' : 'var(--text-primary)' }}>
              {goalMet ? tr.complete.done : `${todayCards} / ${dailyCardGoal}`}
            </span>
          </div>
          <div style={{ height: 8, borderRadius: 50, background: 'var(--border)', overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 50,
              width: `${Math.round(goalProgress * 100)}%`,
              background: 'var(--accent)',
              transition: 'width 0.8s ease',
            }} />
          </div>
          {!goalMet && (
            <p style={{ margin: '5px 0 0', fontSize: 12, color: 'var(--text-secondary)', textAlign: continueUnavailableReason ? 'center' : 'right' }}>
              {continueUnavailableReason ?? tr.complete.moreToGoal(dailyCardGoal - todayCards!)}
            </p>
          )}
        </div>
      )}

      {/* Words learned today + streak */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14,
        animation: 'completeFadeUp 0.4s 0.4s both',
      }}>
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {tr.complete.newWordsToday}{' '}
          <strong style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
            {wordsLearnedToday}
          </strong>
        </span>
        {streakDays != null && streakDays > 0 && (
          <>
            <span style={{ color: 'var(--border)' }}>·</span>
            <span style={{
              fontSize: 13, fontWeight: 600,
              color: streakDays >= 7 ? 'var(--accent)' : 'var(--text-secondary)',
            }}>
              {tr.complete.daysStreak(streakDays)}
            </span>
          </>
        )}
      </div>

      {/* Action buttons — primary always visible, mistakes demoted to text link */}
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center',
        width: '100%', maxWidth: 340,
        animation: 'completeFadeUp 0.4s 0.5s both',
      }}>
        {/* Primary CTA */}
        {primaryIsContinue ? (
          <button className="lc-btn-primary" onClick={onContinueLearning}>
            {tr.complete.continueLearning}
          </button>
        ) : (
          <button className="lc-btn-primary" onClick={onGoHome}>
            {tr.complete.goHome}
          </button>
        )}

        {/* Secondary: Go home only when Continue is the primary */}
        {primaryIsContinue && (
          <button className="lc-btn-secondary" onClick={onGoHome}>
            {tr.complete.goHome}
          </button>
        )}

        {/* Share: generates a branded image and opens the native share sheet.
            Milestone screens get the elevated accent-soft variant (DESIGN_SYSTEM.md
            "Elevated Share button"); standard completions keep the outline style. */}
        <button
          className={milestone ? 'lc-btn-share-elevated' : 'lc-btn-share'}
          onClick={() => (milestone ? setIsMilestoneShareOpen(true) : handleShare())}
          disabled={isSharing}
        >
          <Share2 size={16} />
          {milestone ? tr.complete.milestone.shareLabel : tr.complete.share}
        </button>
        {!milestone && shareStatus && (
          <p style={{ margin: 0, fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center' }}>
            {shareStatus}
          </p>
        )}

        {/* Tertiary: mistakes as a compact text link, not a full button */}
        {mistakes && mistakes.length > 0 && (
          <button className="lc-btn-link" onClick={() => setIsMistakesOpen(true)}>
            {tr.complete.reviewMistakes(mistakes.length)}
          </button>
        )}
      </div>
      </div>

      {mistakes && (
        <MistakesReviewSheet
          isOpen={isMistakesOpen}
          onClose={() => setIsMistakesOpen(false)}
          mistakes={mistakes}
        />
      )}

      {milestone && (
        <MilestoneSharePreviewSheet
          isOpen={isMilestoneShareOpen}
          onClose={() => setIsMilestoneShareOpen(false)}
          labels={milestoneShareLabels}
          defaultField={getCurrentMilestoneField()}
          onShare={handleMilestoneShare}
          isSharing={isSharing}
          shareStatus={shareStatus}
        />
      )}
    </div>
  )
}
